// REVIEW CENTER -> APPROVE TO RESOLVE (owner, 2026-10-26: "a tab in review
// center that shows all issues that are marked unapproved to resolve for
// approval, group approval, or single approval per project").
//
// Every open issue on the project not yet cleared to be worked on. One button
// per row, and the ticked rows together. Both go through the SAME call as the
// issue's own switch and the issue manager's bulk action
// (PUT /issue/approved-to-resolve), so the rules - reviewer side only, every
// project in the set checked - and the one-email-per-person mail are the
// server's, never a second copy here.
//
// "Not approved" includes the issues that never had the field: every issue
// raised before the feature shipped (233 open on prod on 2026-10-26). The
// server's filter is $ne: true for that reason - see GET /issue
// ?approvedToResolve=false and routes/reviewCenter's badge.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Empty, Space, Table, Tag, Tooltip, message } from "antd";
import { CheckOutlined, EyeOutlined } from "@ant-design/icons";
import { getApprovalQueue, setIssuesApprovedToResolve } from "api/issue/issueApi";
import { matchesReviewFilter } from "helpers/reviewFilter";
import { labelFor } from "helpers/artifactPrefix";
import { useBulkSelection, BulkBar } from "components/Coordination/BulkSelect";
import { T } from "pages/Issue/theme";

const navyBtn = { background: T.navy, borderColor: T.navy };
const NAVY_TAG = { background: T.wash, borderColor: "#D4E3EC", color: T.navyD };
const NEUTRAL_TAG = { background: "#F3F4F5", borderColor: "#E8EAEC", color: "#526470" };
const PRIORITY_TAG_STYLES = {
  urgent: { background: T.removedBg, borderColor: "#FAD6D6", color: T.removed2 },
  normal: NAVY_TAG,
  low: NEUTRAL_TAG,
};
const STATUS_WORDS = { open: "Open", pending: "Pending", under_review: "Under review" };

// Opens the issue in a new tab, like every other Review Center tab, so the
// reviewer keeps the queue.
const issueHref = (r) => (r.shareToken ? `/issue/${r.shareToken}/resolve` : `/issue-viewer/${r._id}`);

export default function ApprovalTab({ projectId, filter, reloadKey, onDecided }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState("");

  const loadedOnce = useRef(false);
  const load = useCallback(() => {
    if (!projectId) { setRows([]); return; }
    if (!loadedOnce.current) setLoading(true);
    getApprovalQueue(projectId)
      .then((list) => setRows(list?.issues || []))
      .catch((e) => message.error(e.message || "Failed to load the issues waiting for approval"))
      .finally(() => { setLoading(false); loadedOnce.current = true; });
  }, [projectId, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Approved rows leave the list at once, before the reload confirms it - the
  // reviewer working down the queue should not see the row they just cleared.
  // A refusal is said in the server's own words (who may approve), because the
  // person reading it needs to know it is a permission, not a fault.
  const approve = async (ids) => {
    const list = ids.map(String);
    const res = await setIssuesApprovedToResolve(list, true);
    const gone = new Set(list);
    setRows((prev) => prev.filter((r) => !gone.has(String(r._id))));
    const n = res?.changed ?? list.length;
    message.success(n
      ? `${n} issue${n === 1 ? "" : "s"} approved to resolve - the assignees are told shortly, in one email`
      : "Nothing changed - they were already approved");
    load();
    if (onDecided) onDecided();
  };

  const approveOne = async (row) => {
    setWorking(String(row._id));
    try {
      await approve([row._id]);
    } catch (e) {
      message.error(e.message || "Could not approve the issue");
    } finally {
      setWorking("");
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "displayId",
      width: 90,
      render: (v, r) => (v ? labelFor(v, r.artifactPrefix) : "—"),
    },
    { title: "Title", dataIndex: "title" },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (v, r) => (
        <Space size={4} wrap>
          <Tag style={NEUTRAL_TAG}>{STATUS_WORDS[v] || v || "-"}</Tag>
          {r.needsTriage ? <Tooltip title="Also waiting in the Triage tab"><Tag color="orange">Triage</Tag></Tooltip> : null}
        </Space>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      width: 100,
      render: (v) => (v ? <Tag style={PRIORITY_TAG_STYLES[v] || NEUTRAL_TAG}>{v}</Tag> : "-"),
    },
    {
      title: "Responsible",
      dataIndex: "responsibleUsers",
      width: 200,
      render: (v) => (v || []).map((u) => u?.name || u?.email).filter(Boolean).join(", ") || "-",
    },
    {
      title: "Due",
      dataIndex: "dueDate",
      width: 110,
      render: (v) => (v ? String(v).slice(0, 10) : "-"),
    },
    {
      title: "",
      key: "actions",
      width: 200,
      render: (_, r) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Tooltip title="Open the issue (new tab)">
            <Button size="small" icon={<EyeOutlined />} href={issueHref(r)} target="_blank" rel="noopener noreferrer">
              View
            </Button>
          </Tooltip>
          <Button
            size="small"
            type="primary"
            style={navyBtn}
            icon={<CheckOutlined />}
            loading={working === String(r._id)}
            disabled={!!working}
            onClick={() => approveOne(r)}
            data-testid={`approve-${r._id}`}
          >
            Approve
          </Button>
        </div>
      ),
    },
  ];

  // Hooks before any early return - React counts them per render.
  const visible = rows.filter((r) => matchesReviewFilter(r, filter));
  const sel = useBulkSelection(visible.map((r) => String(r._id)));

  if (!projectId) {
    return <Empty description="Select a project to see the issues waiting for approval" />;
  }

  return (
    <>
      <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 8 }}>
        Open issues on this project that no reviewer has approved to resolve yet.
        Approving clears the work to start; the assignees get one email listing
        everything approved together.
      </div>
      <BulkBar
        sel={sel}
        busy={!!working}
        hint="tick the issues to approve together"
        actions={[
          {
            key: "approve", label: "Approve selected", icon: <CheckOutlined />, primary: true,
            run: async (ids) => {
              try {
                await approve(ids);
              } catch (e) {
                message.error(e.message || "Could not approve the issues");
              }
            },
          },
        ]}
      />
      <Table
        size="small"
        loading={loading}
        dataSource={visible}
        columns={columns}
        rowKey="_id"
        rowSelection={{
          selectedRowKeys: sel.selectedIds,
          onChange: (keys) => sel.replace(keys.map(String)),
        }}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        locale={{ emptyText: <Empty description="Every open issue on this project is approved to resolve" /> }}
      />
    </>
  );
}
